import React, {
  useRef,
  useState,
  useEffect,
  ChangeEvent,
  ComponentProps,
  InputHTMLAttributes,
} from 'react';

import PropTypes from 'prop-types';

import { useForwardedRef } from '@adsk/alloy-react-helpers';
import { MagnifyingGlassIcon, XCircleFilledIcon } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import TextInput from '@adsk/alloy-react-text-input';
import theme, {
  DetailedStylableComponent,
  stylePropType,
} from '@adsk/alloy-react-theme';

import useKeyDown from './hooks/useKeyDown';

type SearchFieldProps = DetailedStylableComponent<
  Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'onChange' | 'value' | 'defaultValue'
  >,
  HTMLInputElement
> &
  Pick<ComponentProps<typeof TextInput>, 'error'> & {
    disabled?: boolean;
    loading?: boolean;
    defaultValue?: string;
    value?: string;
    inlineText?: string;
    onChange?: (value: string) => void;
    onClear?: () => void;
    onSearch?: () => void;
  };

/**
 * A SearchField component with a magnifying glass and a clear input text button
 */
const SearchField = React.forwardRef<HTMLInputElement, SearchFieldProps>(
  (
    {
      disabled = false,
      loading = false,
      style,
      className,
      defaultValue,
      inlineText = '',
      onClear,
      onSearch,
      ...props
    },
    ref,
  ) => {
    const inputRef = useForwardedRef<HTMLInputElement>(ref);
    const [value, setValue] = useState(defaultValue || '');

    const onChange = (e: ChangeEvent<HTMLInputElement>) => {
      props.onChange?.(e.target.value);
      if (props.value === undefined) {
        setValue(e.target.value);
      }
    };

    const magnifyingGlassOnClick = () => {
      inputRef.current?.focus();
      onSearch?.();
    };

    const [rightSideWidth, setRightSideWidth] = useState(0);
    const rightSideWrapperRef = useRef<HTMLDivElement>(null);
    useEffect((): void => {
      if (rightSideWrapperRef?.current) {
        const currentWidth =
          rightSideWrapperRef.current?.getBoundingClientRect().width;
        setRightSideWidth(currentWidth);
      }
    }, [value, props.value, disabled, inlineText, loading]);

    const clearInput = () => {
      props.onChange?.('');
      onClear?.();
      if (props.value === undefined) {
        setValue('');
      }
    };

    useKeyDown(inputRef.current, 'Escape', clearInput);
    useKeyDown(inputRef.current, 'Enter', () => onSearch?.());

    return (
      <div
        role="searchbox"
        aria-label="search field"
        className={className}
        css={[
          {
            position: 'relative',
            width: '100%',
            height: 36,
          },
          style,
        ]}
      >
        <MagnifyingGlassIcon
          size={20}
          role="button"
          onClick={magnifyingGlassOnClick}
          style={{
            color: theme.tokens.colors.icon.tertiary.value,
            left: 10,
            position: 'absolute',
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 2,
            cursor: 'pointer',
            ...((props.value || value) &&
              !disabled && {
                color: theme.tokens.colors.icon.secondary.value,
              }),
            '&:hover': {
              color: theme.tokens.colors.icon.hover.value,
            },
            '&:active': {
              color: theme.tokens.colors.icon.pressed.value,
            },
          }}
        />
        <TextInput
          value={value}
          {...props}
          data-testid="SearchField__input"
          type="search"
          ref={inputRef}
          disabled={disabled}
          onChange={onChange}
          style={{
            ...(disabled && {
              background: theme.tokens.colors.background.primary.value,
            }),
            '& input[type="search"]': {
              paddingLeft: 36,
              paddingRight: rightSideWidth + theme.spacing.XS,
              paddingBottom: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            // Remove default browser clear button
            '& input[type="search"]::-webkit-search-decoration, input[type="search"]::-webkit-search-cancel-button, input[type="search"]::-webkit-search-results-button, input[type="search"]::-webkit-search-results-decoration':
              {
                display: 'none',
              },
          }}
        />
        <div
          ref={rightSideWrapperRef}
          style={{
            position: 'absolute',
            height: 36,
            top: 0,
            right: 0,
            display: 'flex',
            flexFlow: 'row nowrap',
            justifyContent: 'flex-end',
            alignItems: 'center',
          }}
        >
          {inlineText && (
            <span
              style={{
                ...theme.typography.bodySmall,
                marginRight: theme.spacing.XS,
              }}
            >
              {inlineText}
            </span>
          )}
          {loading && (
            <ProgressRing
              data-testid="progress-ring"
              size={ProgressRing.SIZES.XSMALL}
              style={{
                marginRight: theme.spacing.XS,
              }}
            />
          )}
          {(props.value || value) && !disabled && (
            <XCircleFilledIcon
              role="button"
              size={20}
              onClick={(e) => {
                e.preventDefault();
                clearInput();
              }}
              style={[
                {
                  color: theme.tokens.colors.icon.secondary.value,
                  zIndex: 2,
                  marginRight: theme.spacing.XS,
                  cursor: 'pointer',
                  '&:hover': {
                    color: theme.tokens.colors.icon.hover.value,
                  },
                  '&:active': {
                    color: theme.tokens.colors.icon.secondary.value,
                  },
                },
              ]}
            />
          )}
        </div>
      </div>
    );
  },
);

SearchField.displayName = 'SearchField';

SearchField.propTypes = {
  /* Styles applied to the input element */
  style: stylePropType,
  /* Class applied to the input element */
  className: PropTypes.string,
  /* Toggle disabled state */
  disabled: PropTypes.bool,
  /* Toggle loading spinner */
  loading: PropTypes.bool,
  /* initial default text value */
  defaultValue: PropTypes.string,
  /* controlled input value */
  value: PropTypes.string,
  /* Child input's onChange handler */
  onChange: PropTypes.func,
  /* Any text to display at the right of the input,
   * intended to show the number of results returned from the search
   **/
  inlineText: PropTypes.string,
};
export default SearchField;
